/**
 * GTM pipeline type definitions (SKY-389)
 * Mirrors backend app/schemas/pipeline.py
 */

import type { BriefFieldName, BriefSource } from '../constants/brief';

export const PIPELINE_STAGES = [
  'intake',
  'icp',
  'source_enrich',
  'copy',
  'infra',
  'qa',
  'live',
  'replies',
  'done',
] as const;

export type PipelineStage = (typeof PIPELINE_STAGES)[number];

/** Human-friendly column headers for each stage. */
export const STAGE_LABELS: Record<PipelineStage, string> = {
  intake: 'Intake',
  icp: 'ICP',
  source_enrich: 'Source & Enrich',
  copy: 'Copy',
  infra: 'Infra',
  qa: 'QA',
  live: 'Live',
  replies: 'Replies',
  done: 'Done',
};

export interface GTMProject {
  id: string;
  workspace_id: string;
  client_entity_id: string | null;
  client_entity_name: string | null;
  name: string;
  pipeline_stage: PipelineStage;
  needs_human: boolean;
  /** Why the flag is set (SKY-541) — auto-filled from run failures, editable on manual flags. */
  needs_human_reason?: string | null;
  status: string;
  slack_channel_id: string | null;
  notes_md: string | null;
  /** The catalog conference (events.id) this project is about. */
  event_id?: string | null;
  /** The ICP description the icp-plan runs from. */
  icp_text?: string | null;
  /** The ICP the confirmed brief supplies; absent until the brief is confirmed. */
  brief_icp?: string | null;
  created_at: string;
  updated_at: string;
}

/** GET /pipeline/projects/{id}/brief and /portal/campaigns/{id}/brief — mirrors backend CampaignBrief. */
export interface CampaignBrief {
  fields: { name: BriefFieldName; value: string | null; source: BriefSource | null }[];
  confirmed_at: string | null;
  confirmed_by: string | null;
  conference_name: string | null;
  conference_start_missing: boolean;
  plan_summary: string | null;
  people_wanted: number;
}

/** POST /pipeline/projects body — mirrors backend CreateProjectRequest. */
export interface CreateProjectRequest {
  name: string;
  client_entity_id?: string | null;
  slack_channel_id?: string | null;
}

export interface GTMProjectTransition {
  id: string;
  project_id: string;
  user_id: string | null;
  from_stage: PipelineStage | null;
  to_stage: PipelineStage;
  actor: string;
  reason: string | null;
  created_at: string;
}

/** Board grouped by stage — all 9 keys always present. */
export type PipelineBoard = Record<PipelineStage, GTMProject[]>;

export interface RunArtifact {
  id: string | null;
  kind: string;
  label: string;
  uri: string | null;
}

/** One agent_actions audit line — args_keys are keys only, error is a class name. */
export interface RunAction {
  id: string | null;
  tool: string;
  args_keys: string[];
  status: string;
  error: string | null;
  created_at: string | null;
}

/** What a run stopped to ask the operator, and how it was answered. */
export interface RunQuestion {
  text: string;
  kind: 'text' | 'secret' | 'link';
  field?: string | null;
  screenshot_artifact_id?: string | null;
  page_host?: string | null;
  asked_at?: string | null;
  browser_held_until?: string | null;
  answered_at?: string | null;
  answer?: string | null;
  answer_run_id?: string | null;
  /** Answers worth offering as buttons; the text box still takes anything else. */
  options?: string[] | null;
}

/** One picture of the page a run is holding, with the address it is sitting on. */
export interface TakeoverFrame {
  jpeg: Blob;
  url: string;
}

/** One thing a person does to the held page. */
export type TakeoverInput =
  | { action: 'click'; x: number; y: number }
  | { action: 'type'; text: string }
  | { action: 'press'; key: string }
  | { action: 'scroll'; dy: number };

export interface ProjectRun {
  id: string;
  workload: string;
  status: string;
  initiated_by_user_id: string | null;
  created_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  attempt: number;
  error_code: string | null;
  sanitized_error: string | null;
  model: string | null;
  usage_metadata: Record<string, unknown>;
  /** SKY-490 fields are optional: an old backend may omit them during a rolling
   *  deploy — the type states exactly what consumers may rely on (#623 review). */
  input_reference?: string | null;
  input_truncated?: boolean;
  /** Preview only — capped server-side; see output_truncated */
  output_reference?: string | null;
  output_truncated?: boolean;
  artifacts: RunArtifact[];
  actions?: RunAction[];
  /** Run tree: node runs and planner ticks point at their attendee-plan root. */
  task?: string | null;
  parent_run_id?: string | null;
  verdict?: string | null;
  verdict_data?: Record<string, unknown>;
  question?: RunQuestion | null;
}

export interface ProjectRunsResponse {
  runs: ProjectRun[];
  /** Sum across the returned page only — not whole-project spend */
  listed_cost_usd: number;
}

/** One dispatchable play for a project's current stage (SKY-552). */
export interface StageSkill {
  name: string;
  description?: string | null;
}

export interface StageSkillsResponse {
  stage: string;
  skills: StageSkill[];
}

/** Client review state for one lead list, operator-side (SKY-540). */
export interface ProjectLeadList {
  id: string;
  name: string;
  kind: string;
  member_count: number;
  kept_count: number;
  cut_count: number;
  undecided_count: number;
  approved_at?: string | null;
  created_at?: string | null;
}

/** One copy version with the client's decision, operator-side (SKY-540). */
export interface ProjectCopyVariant {
  id: string;
  track: string;
  version: number;
  subject?: string | null;
  decision?: string | null;
  decision_reason?: string | null;
  decided_at?: string | null;
  created_by?: string | null;
}

/** One person on a lead list, operator-side (SKY-544). */
export interface LeadListMember {
  entity_id: string;
  name: string;
  title?: string | null;
  company?: string | null;
  email?: string | null;
  disposition?: string | null;
  crm_bucket?: string | null;
}
